import * as React from "react"
import { createPortal } from "react-dom"
import { MagnifyingGlass, X, Check } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"
import { useModalPortalContainer } from "../modal-portal-context"
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandLoading,
} from "../command"
import { ObjectName } from "../object-name"
import { useCollectionSearch } from "./use-collection-search"
import type { CollectionSearchProps, SearchOption } from "./types"

/**
 * CollectionSearch - An async search combobox for MongoDB collections.
 *
 * Uses a simple inline dropdown (no portals) for reliable behavior inside modals.
 *
 * @example
 * // Basic usage
 * <CollectionSearch
 *   collection="contacts"
 *   value={contactId}
 *   onChange={(option) => setContactId(option?.value || null)}
 *   placeholder="Search contacts..."
 * />
 */
export function CollectionSearch<T = any>({
  collection,
  value,
  onChange,
  filter,
  sort,
  label,
  placeholder = "Search...",
  description,
  disabled = false,
  hideSelected = false,
  addNull = false,
  displayMode = "default",
  formatLabel,
  includeFields,
  menuPlacement = "bottom",
  onBlur,
  limit = 20,
  className,
  clearable = true,
  openOnFocus = true,
  dropdownClassName,
  usePortal = false,
  customSearchFn,
}: CollectionSearchProps<T>) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [isFocused, setIsFocused] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const [portalStyle, setPortalStyle] = React.useState<React.CSSProperties>({})
  const modalContainer = useModalPortalContainer()

  // Inside modal or usePortal → portal to document.body with position:fixed
  // Outside modal + !usePortal → inline absolute (current default)
  const shouldPortal = !!modalContainer || usePortal
  const portalTarget = document.body

  const { options, isLoading, search, loadInitial } = useCollectionSearch({
    collection,
    filter,
    sort,
    limit,
    displayMode,
    formatLabel,
    includeFields,
    addNull,
    customSearchFn,
  })

  // Close on click outside
  React.useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!shouldPortal || !dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setOpen(false)
        setIsFocused(false)
        setInputValue("")
        onBlur?.()
      }
    }

    // Use mousedown to detect before focus changes
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open, onBlur, shouldPortal])

  // Position the portaled dropdown relative to the input
  React.useEffect(() => {
    if (!shouldPortal || !open || !containerRef.current) return

    const updatePosition = () => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const placeAbove = menuPlacement === "top" || (spaceBelow < 320 && spaceAbove > spaceBelow)

      setPortalStyle(
        placeAbove
          ? { position: "fixed", left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 4, zIndex: 10000, pointerEvents: "auto" as const }
          : { position: "fixed", left: rect.left, width: rect.width, top: rect.bottom + 4, zIndex: 10000, pointerEvents: "auto" as const }
      )
    }

    updatePosition()

    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
    return () => {
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [shouldPortal, open, menuPlacement])

  // Handle input change with search
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      setIsFocused(true)
      if (!open) {
        setOpen(true)
        loadInitial()
      }
      search(newValue)
    },
    [search, open, loadInitial]
  )

  // Handle option selection
  const handleSelect = React.useCallback(
    (option: SearchOption<T>) => {
      if (option.disabled) return

      // Handle null option (value is empty string or data._id is null)
      const dataId = (option.data as any)?._id
      if (option.value === "" || dataId === null) {
        onChange(null)
      } else {
        onChange(option)
      }
      setOpen(false)
      setIsFocused(false)
      setInputValue("")
      inputRef.current?.blur()
    },
    [onChange]
  )

  // Handle clear
  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
    },
    [onChange]
  )

  // Handle focus
  const handleFocus = React.useCallback(() => {
    setIsFocused(true)
    if (openOnFocus) {
      setOpen(true)
      loadInitial()
    }
  }, [loadInitial, openOnFocus])

  // Handle click - opens dropdown even when openOnFocus is false
  // This distinguishes user clicks from programmatic focus (e.g., dialog auto-focus)
  const handleClick = React.useCallback(() => {
    setIsFocused(true)
    if (!open) {
      setOpen(true)
      loadInitial()
    }
  }, [open, loadInitial])

  // Handle keyboard
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false)
      setIsFocused(false)
      setInputValue("")
      inputRef.current?.blur()
    }
  }, [])

  // Find selected option in current results
  const selectedOption = value
    ? options.find((opt) => opt.value === value)
    : null

  // Compute the display value for the input when not focused
  const getDisplayValue = React.useCallback((): string => {
    if (hideSelected || !value) return ""

    if (selectedOption) {
      if (typeof selectedOption.label === "string") {
        return selectedOption.label
      }
      return (
        selectedOption.data?.display_name ||
        selectedOption.data?.name ||
        selectedOption.data?.email ||
        "Selected"
      )
    }

    // Return empty string - ObjectName will be shown as overlay for non-string values
    return ""
  }, [hideSelected, value, selectedOption])

  // Determine what value to show in the input
  const displayValue = isFocused ? inputValue : getDisplayValue()

  // Check if we need to show ObjectName overlay (for values not in results)
  const showObjectNameOverlay = !isFocused && value && !selectedOption && !hideSelected

  // Determine dropdown position
  const dropdownPosition = menuPlacement === "top" ? "bottom-full mb-1" : "top-full mt-1"

  return (
    <div ref={containerRef} className="relative space-y-1">
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </label>
      )}

      <div className="relative">
        <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="collection-search-list"
          aria-autocomplete="list"
          disabled={disabled}
          value={displayValue}
          placeholder={showObjectNameOverlay ? "" : placeholder}
          onFocus={handleFocus}
          onClick={handleClick}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-input-background pl-9 pr-8 py-2 text-sm shadow-sm ring-offset-background",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-inset focus:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        />
        {/* ObjectName overlay for values not in current results */}
        {showObjectNameOverlay && (
          <div className="pointer-events-none absolute inset-0 flex items-center pl-9 pr-8">
            <span className="truncate text-sm">
              <ObjectName collection={collection} id={value} />
            </span>
          </div>
        )}
        {/* Clear button */}
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()} // Prevent blur
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm opacity-50 hover:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Dropdown - portal or inline absolute positioning */}
        {open && !shouldPortal && (
          <div
            className={cn(
              "absolute left-0 z-[9999] max-h-[300px] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg",
              dropdownClassName ? dropdownClassName : "right-0",
              dropdownPosition
            )}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false}>
              <CommandList id="collection-search-list" className="max-h-[300px]">
                {isLoading && <CommandLoading>Searching...</CommandLoading>}
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value || "null"}
                      value={option.value}
                      onSelect={() => handleSelect(option)}
                      disabled={option.disabled}
                      className={cn(
                        "cursor-pointer",
                        option.value === value &&
                          "bg-success text-white [&_.text-muted-foreground]:text-white/80"
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 flex-shrink-0",
                          option.value === value ? "opacity-100 text-white" : "opacity-0"
                        )}
                      />
                      <div className="min-w-0 flex-1">{option.label}</div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      {/* Portaled dropdown — renders to modal container or document.body to escape overflow-clipped containers */}
      {open && shouldPortal && createPortal(
        <div
          ref={dropdownRef}
          data-portal-dropdown
          style={portalStyle}
          className="max-h-[300px] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList id="collection-search-list" className="max-h-[300px]">
              {isLoading && <CommandLoading>Searching...</CommandLoading>}
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value || "null"}
                    value={option.value}
                    onSelect={() => handleSelect(option)}
                    disabled={option.disabled}
                    className={cn(
                      "cursor-pointer",
                      option.value === value &&
                        "bg-success text-white [&_.text-muted-foreground]:text-white/80"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        option.value === value ? "opacity-100 text-white" : "opacity-0"
                      )}
                    />
                    <div className="min-w-0 flex-1">{option.label}</div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>,
        portalTarget
      )}

      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
